import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import GenericTable from 'src/components/table/GenericTable';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from 'src/@/components/ui/dropdown-menu';
import PlatformBadge from '../analytics/components/PlatformBadge';
import { MKT_POSTS_DESIGN as d } from './MktPosts.design';
import { STATUS_MAP } from './constants';

function truncate(text, max = 80) {
    if (!text) return '';
    return text.length > max ? `${text.slice(0, max)}...` : text;
}

function formatDate(dateStr, locale) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return String(num);
}

function resultDotClass(post, platform) {
    const status = post.external_ids?.[platform]?.status;
    if (status === 'published') return d.platformResult.published;
    if (status === 'failed') return d.platformResult.failed;
    if (post.status === 'publishing') return d.platformResult.publishing;
    return d.platformResult.pending;
}

export default function MktPostsTable({ posts, capabilities, onOpen, onPreview, onDelete, onPublish, onRetry, onCancel, selectedIds, onToggleOne, onToggleAll, allSelected }) {
    const { t, i18n } = useTranslation('marketing');
    const canPublishAny = capabilities?.canPublishAny !== false;

    const headLabels = [
        {
            id: 'select',
            label: (
                <input
                    type="checkbox"
                    className="w-4 h-4 text-[#7C3AED] border-gray-300 rounded cursor-pointer"
                    checked={allSelected}
                    onChange={(e) => onToggleAll(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                />
            ),
            className: 'w-8 flex-shrink-0 flex items-center'
        },
        { id: 'content', label: t('posts.table.post'), className: 'flex-1 min-w-0 text-xs font-semibold text-gray-500 uppercase tracking-wider' },
        { id: 'platforms', label: t('posts.table.platforms'), className: 'w-40 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:block' },
        { id: 'date', label: t('posts.table.date'), className: 'w-36 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:block' },
        { id: 'status', label: t('posts.table.status'), className: 'w-40 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:block' },
        { id: 'performance', label: t('posts.table.performance'), className: 'w-28 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:block text-right' },
        { id: 'actions', label: '', className: 'w-10 flex-shrink-0' }
    ];

    return (
        <GenericTable headLabels={headLabels}>
            {posts.map((post) => {
                const status = STATUS_MAP[post.status] || STATUS_MAP.draft;
                const media = post.content?.media || [];
                const thumb = media[0];
                const isGraphImport = post.source === 'graph';
                const displayDate = post.published_date || post.scheduled_date;
                const canRetry = post.status === 'failed' || post.status === 'partially_published';
                const canPublishNow = ['draft', 'scheduled', 'failed'].includes(post.status);
                const canCancel = post.status === 'scheduled';

                return (
                    <div key={post._id} className={d.table.row} onClick={() => (onPreview || onOpen)(post)}>
                        <div className="w-8 flex-shrink-0 flex items-center" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                className="w-4 h-4 text-[#7C3AED] border-gray-300 rounded cursor-pointer"
                                checked={selectedIds.has(post._id)}
                                onChange={() => onToggleOne(post._id)}
                            />
                        </div>
                        <div className="flex-1 min-w-0 flex items-center gap-3 mr-4">
                            {thumb ? (
                                thumb.type === 'video' ? (
                                    // eslint-disable-next-line jsx-a11y/media-has-caption
                                    <video src={`${thumb.url}#t=0.1`} preload="metadata" className={d.table.thumb} />
                                ) : (
                                    <img src={thumb.url} alt="" className={d.table.thumb} />
                                )
                            ) : (
                                <div className={d.table.thumbPlaceholder}>
                                    <Iconify icon="eva:file-text-outline" width={18} />
                                </div>
                            )}
                            <div className="min-w-0">
                                <p className={`${d.table.cell} truncate`}>{truncate(post.content?.text) || <span className="text-gray-400 italic">{t('posts.table.noText')}</span>}</p>
                                {isGraphImport && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded mt-0.5">
                                        <Iconify icon="eva:download-outline" width={10} /> {t('posts.table.imported')}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="w-40 flex-shrink-0 mr-4 hidden md:flex flex-col gap-1">
                            {(post.platforms || []).map((p) => (
                                <div key={p} className="flex items-center gap-1.5">
                                    <span className={resultDotClass(post, p)} title={post.external_ids?.[p]?.status || t('posts.table.pending')} />
                                    <PlatformBadge platform={p} />
                                </div>
                            ))}
                        </div>

                        <div className="w-36 flex-shrink-0 mr-4 hidden md:block">
                            <p className={d.table.cellMuted}>{formatDate(displayDate, i18n.language)}</p>
                        </div>

                        <div className="w-40 flex-shrink-0 mr-4 hidden md:block">
                            <span className={d.statusBadge[status.tone]}>
                                <span className={`w-1.5 h-1.5 rounded-full bg-current`} />
                                {t(status.labelKey)}
                            </span>
                            {post.status === 'failed' && post.error_message && (
                                <p className="text-[10px] text-red-500 mt-1 truncate max-w-[9rem]" title={post.error_message}>{post.error_message}</p>
                            )}
                        </div>

                        <div className="w-28 flex-shrink-0 text-right hidden lg:block">
                            {post.status === 'published' && post.performance ? (
                                <div className="flex items-center gap-3 justify-end">
                                    <span className={d.table.cellMuted} title={t('posts.table.likes')}>
                                        <Iconify icon="eva:heart-outline" width={12} className="inline mr-0.5" />
                                        {formatNumber(post.performance.likes)}
                                    </span>
                                </div>
                            ) : (
                                <span className={d.table.cellMuted}>-</span>
                            )}
                        </div>

                        <div className="w-10 flex-shrink-0 flex justify-end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button type="button" className={d.actionMenu.trigger}>
                                        <Iconify icon="eva:more-vertical-outline" width={18} />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 rounded-xl">
                                    <DropdownMenuItem onClick={() => onOpen(post)}>
                                        <Iconify icon={isGraphImport ? 'eva:eye-outline' : 'eva:edit-2-outline'} width={16} className="mr-2" />
                                        {isGraphImport ? t('posts.menu.viewDetails') : t('posts.menu.edit')}
                                    </DropdownMenuItem>
                                    {canPublishNow && (
                                        <DropdownMenuItem onClick={() => onPublish(post)} disabled={!canPublishAny}>
                                            <Iconify icon="eva:paper-plane-outline" width={16} className="mr-2" />
                                            {t('posts.menu.publishNow')}
                                        </DropdownMenuItem>
                                    )}
                                    {canRetry && (
                                        <DropdownMenuItem onClick={() => onRetry(post)} disabled={!canPublishAny}>
                                            <Iconify icon="eva:refresh-outline" width={16} className="mr-2" />
                                            {t('posts.menu.retry')}
                                        </DropdownMenuItem>
                                    )}
                                    {canCancel && (
                                        <DropdownMenuItem onClick={() => onCancel(post)}>
                                            <Iconify icon="eva:slash-outline" width={16} className="mr-2" />
                                            {t('posts.menu.cancelSchedule')}
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => onDelete(post)} className="text-red-600 focus:text-red-600">
                                        <Iconify icon="eva:trash-2-outline" width={16} className="mr-2" />
                                        {t('posts.menu.delete')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                );
            })}
        </GenericTable>
    );
}
